import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError } from 'rxjs';
import { ErrorHandleService } from './error-handle.service';
import {
  HotelDetail,
  HotelDetailAveragePrice,
  HotelDetailAveragePriceResponse,
  HotelDetailFacilitiesResponse,
  HotelDetailOccupancyResponse,
  HotelDetailResponse,
  HotelDetailRevenueResponse,
  HotelOccupancyResponse,
  HotelResponse,
  HotelRevenueResponse,
  TotalGuestResponse,
  HotelFbOutletRevenueResponse
} from '../models/hotel';
import { environment } from '../../../environments/environment.development';

@Injectable({
  providedIn: 'root'
})
export class HotelService {
  private apiUrl = `${environment.APIDEV}`;

  constructor(
    private http: HttpClient,
    private errorHandle: ErrorHandleService
  ) { }

  getHotels(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    sortBy?: string,
    sortOrder?: 'ASC' | 'DESC',
    startDate?: string,
    endDate?: string
  ): Observable<HotelResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());

    if (search) {
      params = params.set('search', search);
    }
    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }
    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelResponse>(`${this.apiUrl}hotel/list`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelOccupancy(
    page: number = 1,
    perPage: number = 10,
    startDate?: string,
    endDate?: string
  ): Observable<HotelOccupancyResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelOccupancyResponse>(`${this.apiUrl}hotel/occupancy`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelRevenue(
    page: number = 1,
    perPage: number = 10,
    startDate?: string,
    endDate?: string
  ): Observable<HotelRevenueResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelRevenueResponse>(`${this.apiUrl}hotel/revenue`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getTotalGuess(
    page: number = 1,
    perPage: number = 10,
    startDate?: string,
    endDate?: string
  ): Observable<TotalGuestResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<TotalGuestResponse>(`${this.apiUrl}hotel/total_guest_room`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelDetail(
    id: number,
    startDate?: string,
    endDate?: string
  ): Observable<HotelDetailResponse> {

    let params = new HttpParams();

    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelDetailResponse>(`${this.apiUrl}hotel/detail/${id}`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelDetailOccupancy(
    id: number,
    startDate?: string,
    endDate?: string
  ): Observable<HotelDetailOccupancyResponse> {

    let params = new HttpParams();

    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelDetailOccupancyResponse>(`${this.apiUrl}hotel/detail/${id}/occupancy`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelDetailAveragePrice(
    id: number,
    startDate?: string,
    endDate?: string
  ): Observable<HotelDetailAveragePriceResponse> {

    let params = new HttpParams();

    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelDetailAveragePriceResponse>(`${this.apiUrl}hotel/detail/${id}/avg_room_price`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelDetailFacilities(
    id: string | number,
    page: number = 1,
    perPage: number = 10,
    search?: string,
    sortBy?: string,
    sortOrder?: string,
    startDate?: string,
    endDate?: string
  ): Observable<HotelDetailFacilitiesResponse> {

    let params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());
    if (search) {
      params = params.set('search', search);
    }

    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }

    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelDetailFacilitiesResponse>(`${this.apiUrl}hotel/detail/${id}/facilities`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelFbOutletRevenue(
    id: string | number,
    page: number = 1,
    perPage: number = 10,
    search?: string,
    sortBy?: string,
    sortOrder?: string,
    startDate?: string,
    endDate?: string
  ): Observable<HotelFbOutletRevenueResponse> {

    let params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());
    if (search) {
      params = params.set('search', search);
    }

    if (sortBy) {
      params = params.set('sortBy', sortBy);
    }

    if (sortOrder) {
      params = params.set('sortOrder', sortOrder);
    }

    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelFbOutletRevenueResponse>(`${this.apiUrl}hotel/detail/${id}/fb-outlet-revenue`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }

  getHotelDetailRevenue(
    id: string | number,
    startDate?: string,
    endDate?: string
  ): Observable<HotelDetailRevenueResponse> {
    let params = new HttpParams();

    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get<HotelDetailRevenueResponse>(`${this.apiUrl}hotel/detail/${id}/revenue`, {
      params,
      ...this.errorHandle._httpOptions()
    }).pipe(
      catchError(this.errorHandle.errorHandl)
    );
  }



}
